import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, NavLink, Navigate, useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import UnifiedDashboard from './pages/UnifiedDashboard';
import Transactions from './pages/Transactions';
import Receipts from './pages/Receipts';
import Mileage from './pages/Mileage';
import WriteOffs from './pages/WriteOffs';
import Reports from './pages/Reports';
import ScheduleCPreview from './pages/ScheduleCPreview';
import Documents from './pages/Documents';
import DocumentCenter from './pages/DocumentCenter';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import Pricing from './pages/Pricing';
import Admin from './pages/Admin';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Terms from './pages/Terms';
import ExpensesManage from './pages/ExpensesManage';
import IncomeManage from './pages/IncomeManage';
import ExpensesReports from './pages/ExpensesReports';
import ExpensesDocuments from './pages/ExpensesDocuments';
import DigitalShoebox from './pages/DigitalShoebox';
import Budgets from './pages/Budgets';
import Goals from './pages/Goals';
import Subscriptions from './pages/Subscriptions';
import MergedView from './pages/MergedView';
import PersonalReports from './pages/PersonalReports';
import QuarterlyTaxes from './pages/QuarterlyTaxes';
import Invoices from './pages/Invoices';
import Clients from './pages/Clients';
import Contractors from './pages/Contractors';
import Projects from './pages/Projects';
import ProfitLoss from './pages/ProfitLoss';
import RecurringIncome from './pages/RecurringIncome';
import BusinessHealthScore from './pages/BusinessHealthScore';
import AdvancedAnalytics from './pages/AdvancedAnalytics';
// BusinessProfiles removed - single business per user for simplicity
import DemoBanner from './components/DemoBanner';
import NotificationBell from './components/NotificationBell';
import SEOHead from './components/SEOHead';
import Analytics from './components/Analytics';
import { OnboardingProvider } from './components/OnboardingTour';
import { getCurrentUser, logout, checkAdminStatus } from './lib/api';
import { DataContextProvider } from './contexts/DataContext';
import { Toaster } from './components/ui/sonner';
import { Button } from './components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './components/ui/dropdown-menu';
import {
  LayoutDashboard,
  Receipt,
  Car,
  FileText,
  PiggyBank,
  BarChart3,
  Menu,
  X,
  Settings as SettingsIcon,
  FolderOpen,
  User,
  LogOut,
  Crown,
  Shield,
  LogIn,
  Lock,
  Sparkles,
  ChevronDown,
  Briefcase,
  Home,
  Wallet,
  Target,
  RefreshCw,
  Layers,
  Calculator,
  FileCheck,
  Users,
  UserCheck,
  FolderKanban,
  TrendingUp,
  Activity
} from 'lucide-react';

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Auth Prompt Context for showing login modal
const AuthPromptContext = createContext(null);

export const useAuthPrompt = () => useContext(AuthPromptContext);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const res = await getCurrentUser();
      setUser(res.data);
      setIsAuthenticated(true);
      
      // Check admin status
      try {
        const adminRes = await checkAdminStatus();
        setIsAdmin(adminRes.data.is_admin);
      } catch {
        setIsAdmin(false);
      }
    } catch (error) {
      // Not authenticated - this is normal for preview mode
      setUser(null);
      setIsAuthenticated(false);
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      // Continue with logout even if API fails
    }
    setUser(null);
    setIsAuthenticated(false);
    setIsAdmin(false);
    window.location.href = '/dashboard';
  };

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, isAdmin, loading, checkAuth, logout: handleLogout }}>
      {children}
    </AuthContext.Provider>
  );
}

// Auth Prompt Provider - shows modal when user tries to do protected action
function AuthPromptProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const [promptFeature, setPromptFeature] = useState('');
  const navigate = useNavigate();

  const showAuthPrompt = (feature = '') => {
    setPromptFeature(feature);
    setIsOpen(true);
  };

  const handleSignIn = () => {
    setIsOpen(false);
    navigate('/login');
  };

  return (
    <AuthPromptContext.Provider value={{ showAuthPrompt }}>
      {children}
      
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-md border-border">
          <DialogHeader>
            <div className="mx-auto w-14 h-14 rounded-full bg-[hsl(var(--accent))]/10 flex items-center justify-center mb-4">
              <Lock className="w-7 h-7 text-[hsl(var(--accent))]" />
            </div>
            <DialogTitle className="text-center text-xl">
              Sign in to {promptFeature || 'Continue'}
            </DialogTitle>
            <DialogDescription className="text-center">
              Create a free account to save your data and unlock all features
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            {/* Benefits list */}
            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              <p className="text-sm font-medium flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[hsl(var(--accent))]" />
                Free account includes:
              </p>
              <ul className="text-sm text-muted-foreground space-y-1.5 ml-6">
                <li>Track unlimited expenses</li>
                <li>Scan receipts with AI</li>
                <li>Log mileage automatically</li>
                <li>Generate tax reports</li>
              </ul>
            </div>
            
            <Button 
              className="w-full h-11 bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-base"
              onClick={handleSignIn}
              data-testid="auth-prompt-signin-btn"
            >
              <LogIn className="w-5 h-5 mr-2" />
              Sign in with Google
            </Button>
            
            <p className="text-xs text-center text-muted-foreground">
              By signing in, you agree to our{' '}
              <a href="/terms" className="underline hover:text-foreground">Terms</a>
              {' '}and{' '}
              <a href="/privacy" className="underline hover:text-foreground">Privacy Policy</a>
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </AuthPromptContext.Provider>
  );
}

// Business section navigation items - Clean & Organized
const businessNavItems = [
  { path: '/business/dashboard', label: 'Dashboard', icon: LayoutDashboard, testId: 'nav-business-dashboard' },
  { path: '/business/health-score', label: 'Health Score', icon: Activity, testId: 'nav-business-health' },
  { path: '/business/analytics', label: 'Analytics', icon: BarChart3, testId: 'nav-business-analytics' },
  { path: '/business/profit-loss', label: 'Profit & Loss', icon: TrendingUp, testId: 'nav-business-pnl' },
  { path: '/business/expenses/manage', label: 'Expenses', icon: Receipt, testId: 'nav-business-expenses' },
  { path: '/business/income', label: 'Income', icon: PiggyBank, testId: 'nav-business-income' },
  { path: '/business/recurring-income', label: 'Recurring Income', icon: RefreshCw, testId: 'nav-business-recurring' },
  { path: '/business/clients', label: 'Clients', icon: Users, testId: 'nav-business-clients' },
  { path: '/business/invoices', label: 'Invoices', icon: FileCheck, testId: 'nav-business-invoices' },
  { path: '/business/projects', label: 'Projects', icon: FolderKanban, testId: 'nav-business-projects' },
  { path: '/business/contractors', label: '1099 Contractors', icon: UserCheck, testId: 'nav-business-contractors' },
  { path: '/business/docs', label: 'Documents', icon: FolderOpen, testId: 'nav-business-docs' },
  { path: '/business/mileage', label: 'Mileage', icon: Car, testId: 'nav-business-mileage' },
  { path: '/business/quarterly-taxes', label: 'Quarterly Taxes', icon: Calculator, testId: 'nav-business-quarterly-taxes' },
  { path: '/business/reports', label: 'Reports', icon: BarChart3, testId: 'nav-business-reports' },
];

// Personal section navigation items - Simplified for personal finance
const personalNavItems = [
  { path: '/personal/dashboard', label: 'Dashboard', icon: LayoutDashboard, testId: 'nav-personal-dashboard' },
  { path: '/personal/expenses/manage', label: 'Expenses', icon: Receipt, testId: 'nav-personal-expenses' },
  { path: '/personal/income', label: 'Income', icon: PiggyBank, testId: 'nav-personal-income' },
  { path: '/personal/budgets', label: 'Budgets', icon: Wallet, testId: 'nav-personal-budgets' },
  { path: '/personal/goals', label: 'Goals', icon: Target, testId: 'nav-personal-goals' },
  { path: '/personal/subscriptions', label: 'Subscriptions', icon: RefreshCw, testId: 'nav-personal-subscriptions' },
  { path: '/personal/docs', label: 'Documents', icon: FolderOpen, testId: 'nav-personal-docs' },
  { path: '/personal/reports', label: 'Reports', icon: BarChart3, testId: 'nav-personal-reports' },
];

// Combined view - shown at top level
const combinedNavItem = { path: '/combined', label: 'Combined Overview', icon: Layers, testId: 'nav-combined' };

// Separate component for sub-navigation items to avoid babel plugin recursion issues
function SubNavItems({ subItems, onClose }) {
  return (
    <div className="ml-4 pl-4 border-l border-border space-y-1">
      {subItems.map((sub) => (
        <NavLink
          key={sub.path}
          to={sub.path}
          className={({ isActive }) => `nav-item text-sm py-2 ${isActive ? 'active' : ''}`}
          onClick={onClose}
          data-testid={sub.testId}
        >
          <span>{sub.label}</span>
        </NavLink>
      ))}
    </div>
  );
}

function NavItemWithSubs({ item, onClose }) {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  
  const subItems = item.subItems || [];
  const hasSubItems = subItems.length > 0;
  const isSubActive = hasSubItems && subItems.some(sub => location.pathname === sub.path);
  const isActive = location.pathname === item.path || isSubActive;
  
  useEffect(() => {
    if (isSubActive) setIsOpen(true);
  }, [isSubActive]);

  if (!hasSubItems) {
    return (
      <NavLink
        to={item.path}
        className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
        onClick={onClose}
        data-testid={item.testId}
      >
        <item.icon className="w-5 h-5" />
        <span>{item.label}</span>
      </NavLink>
    );
  }

  return (
    <div className="space-y-1">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`nav-item w-full justify-between ${isActive ? 'active' : ''}`}
        data-testid={item.testId}
      >
        <div className="flex items-center gap-3">
          <item.icon className="w-5 h-5" />
          <span>{item.label}</span>
        </div>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && <SubNavItems subItems={subItems} onClose={onClose} />}
    </div>
  );
}

// Section component for Business/Personal with collapsible nav
function NavSection({ title, icon: Icon, items, isExpanded, onToggle, onClose, colorClass }) {
  const location = useLocation();
  const isAnyActive = items.some(item => {
    if (item.subItems) {
      return item.subItems.some(sub => location.pathname === sub.path);
    }
    return location.pathname === item.path;
  });

  return (
    <div className="space-y-1">
      <button
        onClick={onToggle}
        className={`nav-item w-full justify-between ${isAnyActive ? 'active' : ''} ${colorClass}`}
        data-testid={`nav-section-${title.toLowerCase()}`}
      >
        <div className="flex items-center gap-3">
          <Icon className="w-5 h-5" />
          <span className="font-semibold">{title}</span>
        </div>
        <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
      </button>
      {isExpanded && (
        <div className="ml-2 pl-2 border-l border-border/50 space-y-1">
          {items.map((item) => (
            <NavItemWithSubs key={item.path} item={item} onClose={onClose} />
          ))}
        </div>
      )}
    </div>
  );
}

function Sidebar({ isOpen, onClose }) {
  const auth = useAuth();
  const location = useLocation();
  const [businessExpanded, setBusinessExpanded] = useState(true);
  const [personalExpanded, setPersonalExpanded] = useState(false);
  
  // Auto-expand section based on current path
  useEffect(() => {
    if (location.pathname.startsWith('/business')) {
      setBusinessExpanded(true);
    } else if (location.pathname.startsWith('/personal')) {
      setPersonalExpanded(true);
    }
  }, [location.pathname]);
  
  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <aside
        className={`
          fixed md:sticky top-0 left-0 z-50 h-screen
          w-72 bg-card border-r border-border
          transform transition-transform duration-300 ease-in-out
          md:transform-none
          ${isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        `}
        data-testid="main-sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-border">
            <div className="flex items-center gap-2">
              <img 
                src="/logo.png" 
                alt="Zero Deficit Sync Logo" 
                className="w-10 h-10 object-contain"
              />
              <span className="text-lg font-bold font-['Space_Grotesk']">Zero Deficit Sync</span>
            </div>
            <button
              className="md:hidden p-2 hover:bg-muted rounded-lg"
              onClick={onClose}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Navigation with Business and Personal sections */}
          <nav className="flex-1 px-3 py-4 space-y-4 overflow-y-auto">
            {/* Combined Overview - Top Level */}
            <NavLink
              to="/combined"
              className={({ isActive }) => `nav-item ${isActive ? 'active' : ''} mb-4 border border-[hsl(var(--accent))]/30 hover:border-[hsl(var(--accent))]/50`}
              onClick={onClose}
              data-testid="nav-combined"
            >
              <Layers className="w-5 h-5 text-[hsl(var(--accent))]" />
              <span className="font-medium">Combined View</span>
            </NavLink>

            {/* Business Section */}
            <NavSection
              title="Business"
              icon={Briefcase}
              items={businessNavItems}
              isExpanded={businessExpanded}
              onToggle={() => setBusinessExpanded(!businessExpanded)}
              onClose={onClose}
              colorClass="text-emerald-400 hover:text-emerald-300"
            />
            
            {/* Personal Section */}
            <NavSection
              title="Personal"
              icon={Home}
              items={personalNavItems}
              isExpanded={personalExpanded}
              onToggle={() => setPersonalExpanded(!personalExpanded)}
              onClose={onClose}
              colorClass="text-blue-400 hover:text-blue-300"
            />
          </nav>
          
          {/* Footer - Simplified without profile info */}
          <div className="px-3 py-4 border-t border-border space-y-1">
            {auth?.isAuthenticated ? (
              <>
                <NavLink
                  to="/settings"
                  className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                  onClick={onClose}
                  data-testid="nav-settings-link"
                >
                  <SettingsIcon className="w-5 h-5" />
                  <span>Settings</span>
                </NavLink>
                {auth?.isAdmin && (
                  <NavLink
                    to="/admin"
                    className={({ isActive }) => `nav-item ${isActive ? 'active' : ''} text-[hsl(var(--accent))]`}
                    onClick={onClose}
                    data-testid="nav-admin-link"
                  >
                    <Shield className="w-5 h-5" />
                    <span>Admin</span>
                  </NavLink>
                )}
              </>
            ) : (
              <NavLink
                to="/login"
                className="nav-item bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/20"
                onClick={onClose}
                data-testid="nav-login-link"
              >
                <LogIn className="w-5 h-5" />
                <span>Sign In</span>
              </NavLink>
            )}
          </div>
        </div>
      </aside>
    </>
  );
}

// Header component with login/profile button and context indicator
function Header({ onMenuClick }) {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Determine current context from URL
  const isPersonalContext = location.pathname.startsWith('/personal');
  const contextLabel = isPersonalContext ? 'Personal' : 'Business';
  const contextColor = isPersonalContext ? 'text-blue-400 bg-blue-400/10 border-blue-400/30' : 'text-emerald-400 bg-emerald-400/10 border-emerald-400/30';
  const ContextIcon = isPersonalContext ? Home : Briefcase;
  
  return (
    <header
      className="sticky top-0 z-30 h-14 bg-background/80 backdrop-blur border-b border-border"
      data-testid="app-header"
    >
      <div className="flex items-center justify-between h-full px-4">
        {/* Left: Menu button (mobile) */}
        <button
          className="md:hidden p-2 hover:bg-muted rounded-lg"
          onClick={onMenuClick}
          data-testid="mobile-menu-button"
        >
          <Menu className="w-5 h-5" />
        </button>
        
        {/* Center: Logo (mobile only) */}
        <div className="md:hidden flex items-center gap-2">
          <img 
            src="/logo.png" 
            alt="Zero Deficit Sync Logo" 
            className="w-8 h-8 object-contain"
          />
          <span className="font-bold font-['Space_Grotesk']">Zero Deficit Sync</span>
        </div>
        
        {/* Desktop: Context Indicator */}
        <div className="hidden md:flex items-center gap-3">
          <div 
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${contextColor} transition-colors`}
            data-testid="context-indicator"
          >
            <ContextIcon className="w-4 h-4" />
            <span className="text-sm font-medium">{contextLabel} Mode</span>
          </div>
        </div>
        
        {/* Right: Notifications & Auth button */}
        <div className="flex items-center gap-2">
          {/* Mobile context indicator */}
          <div 
            className={`md:hidden flex items-center gap-1.5 px-2 py-1 rounded-full border ${contextColor}`}
            data-testid="context-indicator-mobile"
          >
            <ContextIcon className="w-3.5 h-3.5" />
            <span className="text-xs font-medium">{contextLabel}</span>
          </div>
          
          {/* Notification Bell - only show when authenticated */}
          {auth?.isAuthenticated && <NotificationBell />}
          
          {auth?.isAuthenticated ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="flex items-center gap-2 px-3"
                  data-testid="user-menu-trigger"
                >
                  {auth.user?.picture ? (
                    <img 
                      src={auth.user.picture} 
                      alt={auth.user.name} 
                      className="w-7 h-7 rounded-full"
                    />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-[hsl(var(--accent))]/20 flex items-center justify-center">
                      <User className="w-4 h-4 text-[hsl(var(--accent))]" />
                    </div>
                  )}
                  <span className="hidden sm:inline text-sm font-medium max-w-[120px] truncate">
                    {auth.user?.name?.split(' ')[0] || 'Account'}
                  </span>
                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{auth.user?.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{auth.user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/profile')}>
                  <User className="w-4 h-4 mr-2" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/settings')}>
                  <SettingsIcon className="w-4 h-4 mr-2" />
                  Settings
                </DropdownMenuItem>
{/* Upgrade link hidden - all features now available to all users */}
                {auth.isAdmin && (
                  <DropdownMenuItem onClick={() => navigate('/admin')}>
                    <Shield className="w-4 h-4 mr-2" />
                    Admin Panel
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={auth.logout}
                  className="text-[hsl(var(--destructive))]"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button 
              onClick={() => navigate('/login')}
              className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90"
              data-testid="header-login-btn"
            >
              <LogIn className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Sign In</span>
              <span className="sm:hidden">Login</span>
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}

function AppLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  return (
    <div className="min-h-screen bg-background">
      <div className="flex">
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        
        <main className="flex-1 min-h-screen flex flex-col">
          <Header onMenuClick={() => setSidebarOpen(true)} />
          <DemoBanner />
          
          {/* Page content */}
          <div className="max-w-[1400px] mx-auto flex-1 w-full">
            {children}
          </div>
        </main>
      </div>
      <Toaster position="bottom-right" />
    </div>
  );
}

// Pages that require authentication (profile, settings, admin)
function ProtectedPage({ children }) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AuthPromptProvider>
          <OnboardingProvider>
            <SEOHead />
            <Analytics />
            <Routes>
              {/* Public auth pages - no layout */}
              <Route path="/login" element={<Login />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              
              {/* App routes - browsable without login */}
              <Route path="/*" element={
                <DataContextProvider>
                  <AppLayout>
                    <Routes>
                    <Route path="/" element={<Navigate to="/business/dashboard" replace />} />
                    
                    {/* Business Routes - Clean Structure */}
                    <Route path="/business/dashboard" element={<UnifiedDashboard context="business" />} />
                    <Route path="/business/health-score" element={<BusinessHealthScore />} />
                    <Route path="/business/analytics" element={<AdvancedAnalytics />} />
                    <Route path="/business/profit-loss" element={<ProfitLoss />} />
                    <Route path="/business/expenses/manage" element={<ExpensesManage context="business" />} />
                    <Route path="/business/income" element={<IncomeManage context="business" />} />
                    <Route path="/business/recurring-income" element={<RecurringIncome />} />
                    <Route path="/business/clients" element={<Clients />} />
                    <Route path="/business/invoices" element={<Invoices />} />
                    <Route path="/business/projects" element={<Projects />} />
                    <Route path="/business/contractors" element={<Contractors />} />
                    <Route path="/business/docs" element={<DocumentCenter context="business" />} />
                    <Route path="/business/mileage" element={<Mileage context="business" />} />
                    <Route path="/business/quarterly-taxes" element={<QuarterlyTaxes />} />
                    <Route path="/business/reports" element={<Reports context="business" />} />
                    <Route path="/business/schedule-c" element={<ScheduleCPreview />} />
                    
                    {/* Business Legacy Routes - Redirects */}
                    <Route path="/business/transactions" element={<Navigate to="/business/expenses/manage" replace />} />
                    <Route path="/business/receipts" element={<Navigate to="/business/docs?tab=receipts" replace />} />
                    <Route path="/business/writeoffs" element={<Navigate to="/business/reports" replace />} />
                    <Route path="/business/documents" element={<Navigate to="/business/docs" replace />} />
                    <Route path="/business/document-center" element={<Navigate to="/business/docs" replace />} />
                    <Route path="/business/expenses/dashboard" element={<Navigate to="/business/dashboard" replace />} />
                    <Route path="/business/expenses/reports" element={<Navigate to="/business/reports" replace />} />
                    <Route path="/business/expenses/documents" element={<Navigate to="/business/docs" replace />} />
                    <Route path="/business/shoebox" element={<Navigate to="/business/docs?tab=email" replace />} />
                    
                    {/* Personal Routes - Clean Structure */}
                    <Route path="/personal/dashboard" element={<UnifiedDashboard context="personal" />} />
                    <Route path="/personal/expenses/manage" element={<ExpensesManage context="personal" />} />
                    <Route path="/personal/income" element={<IncomeManage context="personal" />} />
                    <Route path="/personal/budgets" element={<Budgets />} />
                    <Route path="/personal/goals" element={<Goals />} />
                    <Route path="/personal/subscriptions" element={<Subscriptions />} />
                    <Route path="/personal/docs" element={<DocumentCenter context="personal" />} />
                    <Route path="/personal/reports" element={<PersonalReports />} />
                    
                    {/* Combined View Route */}
                    <Route path="/combined" element={<MergedView />} />
                    
                    {/* Personal Legacy Routes - Redirects */}
                    <Route path="/personal/transactions" element={<Navigate to="/personal/expenses/manage" replace />} />
                    <Route path="/personal/receipts" element={<Navigate to="/personal/docs?tab=receipts" replace />} />
                    <Route path="/personal/mileage" element={<Mileage context="personal" />} />
                    <Route path="/personal/documents" element={<Navigate to="/personal/docs" replace />} />
                    <Route path="/personal/document-center" element={<Navigate to="/personal/docs" replace />} />
                    <Route path="/personal/expenses/dashboard" element={<Navigate to="/personal/dashboard" replace />} />
                    <Route path="/personal/expenses/reports" element={<Navigate to="/personal/reports" replace />} />
                    <Route path="/personal/expenses/documents" element={<Navigate to="/personal/docs" replace />} />
                    <Route path="/personal/shoebox" element={<Navigate to="/personal/docs?tab=email" replace />} />
                    
                    {/* Root Legacy Routes */}
                    <Route path="/dashboard" element={<Navigate to="/business/dashboard" replace />} />
                    <Route path="/transactions" element={<Navigate to="/business/expenses/manage" replace />} />
                    <Route path="/receipts" element={<Navigate to="/business/docs?tab=receipts" replace />} />
                    <Route path="/mileage" element={<Navigate to="/business/mileage" replace />} />
                    <Route path="/writeoffs" element={<Navigate to="/business/reports" replace />} />
                    <Route path="/documents" element={<Navigate to="/business/docs" replace />} />
                    <Route path="/reports" element={<Navigate to="/business/reports" replace />} />
                    <Route path="/expenses/dashboard" element={<Navigate to="/business/dashboard" replace />} />
                    <Route path="/expenses/manage" element={<Navigate to="/business/expenses/manage" replace />} />
                    <Route path="/expenses/reports" element={<Navigate to="/business/reports" replace />} />
                    <Route path="/expenses/documents" element={<Navigate to="/business/docs" replace />} />
                    <Route path="/income" element={<Navigate to="/business/income" replace />} />
                    <Route path="/shoebox" element={<Navigate to="/business/docs?tab=email" replace />} />
                    
                    {/* Static pages */}
                    <Route path="/pricing" element={<Pricing />} />
                    <Route path="/privacy" element={<PrivacyPolicy />} />
                    <Route path="/terms" element={<Terms />} />
                    
                    {/* Protected pages */}
                    <Route path="/profile" element={<ProtectedPage><Profile /></ProtectedPage>} />
                    <Route path="/settings" element={<ProtectedPage><Settings /></ProtectedPage>} />
                    <Route path="/admin" element={<ProtectedPage><Admin /></ProtectedPage>} />
                  </Routes>
                </AppLayout>
              </DataContextProvider>
              } />
            </Routes>
          </OnboardingProvider>
        </AuthPromptProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
